import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import nds from '@nerdwallet/base-styles/classes';
import { Li, Link, List, Text } from '@nerdwallet/currency';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import {
  CONTENT_TABS,
  PREHEADER_TYPE,
  MarketplaceProductCardImpression,
  BaseMarketplaceProductCard,
} from '@nerdwallet/react-marketplace-product-card';
import { DISPLAY_PAGES } from '@nerdwallet/react-banking-product-cta';
import {
  PRODUCT_TYPES,
  BANKING_SUB_PRODUCT_TYPES,
  INVESTING_SUB_PRODUCT_TYPES,
} from '@nerdwallet/marketplace/constants';
import {
  getApplyLink,
  getLegacyId,
  getProductType,
  getSubProductType,
  getId,
} from '@nerdwallet/marketplace/selectors/common';
import { getLegacyId as getCreditCardLegacyId } from '@nerdwallet/marketplace/selectors/credit-cards';
import { isNonMonetizing } from '@nerdwallet/marketplace/utils';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';
import { withMarketplaceContext } from '@nerdwallet/structured-content/contexts/MarketplaceContext';
import * as compareCreditCardsBarActions from '@nerdwallet/compare-credit-cards-bar/actions';

import {
  boolCheckboxSpec,
  dataDependencyTypePicker,
  dataDependencyIdInput,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
  group,
} from '../../lib/editing-configs';
import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import { getSlug } from '../../../server/config-fetcher';
import { DATA_DEPENDENCY_TYPES } from '../../lib/constants';
import { getOfferById } from '../../lib/helpers';

import styles from './marketplace-product-card.module.less';
import { withFilters } from '../filters/with-filters';

const PRODUCT_TYPE_NAME_MAPPING = {
  [PRODUCT_TYPES.CREDIT_CARDS]: 'Card',
  [BANKING_SUB_PRODUCT_TYPES.CHECKING]: 'Account',
  [PRODUCT_TYPES.BANKING]: 'Bank',
  [BANKING_SUB_PRODUCT_TYPES.CD]: 'CD',
  [BANKING_SUB_PRODUCT_TYPES.SAVINGS]: 'Account',
  [BANKING_SUB_PRODUCT_TYPES.MONEY_MARKET]: 'Account',
  [INVESTING_SUB_PRODUCT_TYPES.BROKER]: 'Broker',
  [INVESTING_SUB_PRODUCT_TYPES.INVESTMENT_ADVISOR]: 'Robo-advisor',
  [PRODUCT_TYPES.PERSONAL_LOANS]: 'Lender',
  [PRODUCT_TYPES.EDUCATION_LOANS]: 'Lender',
};

const PRODUCT_TYPE_UNSTRUCTURED_VERTICAL = {
  AUTOINSURANCE: 'auto_insurance',
  TAXES: 'taxes',
  TRAVEL: 'travel',
};

const getProductName = (entity) => {
  const productType = getProductType(entity);
  switch (productType) {
    case PRODUCT_TYPES.BANKING:
    case PRODUCT_TYPES.INVESTING:
      return (
        PRODUCT_TYPE_NAME_MAPPING[getSubProductType(entity)] ||
        PRODUCT_TYPE_NAME_MAPPING[productType]
      );

    default:
      return PRODUCT_TYPE_NAME_MAPPING[productType];
  }
};

const MarketplaceProductCard = ({
  badgeText,
  ctaHref,
  ctaSubtext,
  ctaText,
  data,
  heading,
  history,
  identity,
  includeBadge,
  includeWhyThisProduct,
  isUnstructuredVertical,
  keyFacts,
  omitRating,
  productCardImage,
  productType,
  dataDependency,
  starRating,
  whyThisProductMethodologyUrl,
  whyThisProductText,
  whyThisProductTypeOverride,
  saveToCompare,
  removeFromCompare,
  includeCompareButton,
  includeWhyNerdsLoveIt,
  whyNerdsLoveItMethodologyUrl,
  whyNerdsLoveItText,
  includeMarginTop,
  marginTopDesktop,
  marginTopTablet,
  marginTopMobile,
  includeMarginBottom,
  marginBottomDesktop,
  marginBottomTablet,
  marginBottomMobile,
  size,
  identifier,
  scrollOffsetDesktop,
  scrollOffsetTablet,
  scrollOffsetMobile,
  location,
}) => {
  const offers = Object.values(_.get(data, 'value', {}));
  const requestedId = _.get(dataDependency, 'data.ids[0].id', null);

  let marketplaceEntity = getOfferById(requestedId, offers);

  const hideRoot = _.isEmpty(marketplaceEntity) && !isUnstructuredVertical;

  const scrollMargin = useMemo(
    () =>
      identifier &&
      ({
        lg: scrollOffsetDesktop,
        md: scrollOffsetTablet,
        sm: scrollOffsetMobile,
      }[size] ??
        scrollOffsetDesktop ??
        scrollOffsetTablet ??
        scrollOffsetMobile ??
        0),
    [
      size,
      identifier,
      scrollOffsetDesktop,
      scrollOffsetTablet,
      scrollOffsetMobile,
    ]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // We have to emulate the scroll behavior
    // Try to find element with the same identifier and scroll to it
    const hash = location.hash.slice(1);
    if (identifier === hash) {
      document.getElementById(identifier)?.scrollIntoView();
    }
  }, [identifier, location.hash]);

  const marginTop = useMemo(
    () =>
      includeMarginTop &&
      {
        lg: marginTopDesktop,
        md: marginTopTablet,
        sm: marginTopMobile,
      }[size],
    [includeMarginTop, size, marginTopDesktop, marginTopTablet, marginTopMobile]
  );

  const marginBottom = useMemo(
    () =>
      includeMarginBottom &&
      {
        lg: marginBottomDesktop,
        md: marginBottomTablet,
        sm: marginBottomMobile,
      }[size],
    [
      includeMarginBottom,
      size,
      marginBottomDesktop,
      marginBottomTablet,
      marginBottomMobile,
    ]
  );

  if (hideRoot) {
    return null;
  }

  const cardIdentifier = _.isEmpty(marketplaceEntity)
    ? heading
    : getProductName(marketplaceEntity);

  // Allow heading override for BNK.
  const isBanking = dataDependency.type === PRODUCT_TYPES.BANKING;
  const headingProps = isBanking && heading ? { heading } : {};

  const badgeProps = includeBadge
    ? {
        hasPreheader: true,
        preheaderText: badgeText,
        preheaderType: PREHEADER_TYPE.BADGE,
      }
    : {};

  const isComparisonActive = includeCompareButton && !isUnstructuredVertical;

  if (isComparisonActive && marketplaceEntity) {
    // We need to emulate credit cards logic for comparison to match
    marketplaceEntity = {
      ...(marketplaceEntity || {}),
      details: {
        ...(marketplaceEntity?.details || {}),
        legacy_id: marketplaceEntity?.details?.legacy_id?.value
          ? marketplaceEntity?.details?.legacy_id
          : {
              value: marketplaceEntity.id,
              type: typeof marketplaceEntity.id,
            },
      },
    };
  }

  const compareButtonProps = isComparisonActive
    ? {
        _hasCcCompareButton: includeCompareButton,
        /**
         * We have to define handling comparison functionality.
         * The react-components library that we use for MarketplaceProductCard component
         * redefine custom saving logic with an empty function. We need to save and remove
         * the stuff on our side
         */
        // eslint-disable-next-line no-shadow
        _handleCcCompareClick: (_, compared) =>
          compared
            ? // We can just pass card ids. We don't need to load them
              // We use structured data content for the data in the container component
              // See useComparisonPanelProps hook to check the logic
              saveToCompare({
                cards: [{ id: getCreditCardLegacyId(marketplaceEntity) }],
              })
            : removeFromCompare(getCreditCardLegacyId(marketplaceEntity)),
      }
    : {};

  // hack for SHOP-9808 + hack for MR-2915
  const slug = getSlug(_.get(history, 'location.pathname', ''));
  const shouldUpdateSharedId =
    dataDependency.type === DATA_DEPENDENCY_TYPES.CREDIT_CARDS &&
    slug === 'awards-credit-cards-2020' &&
    getLegacyId(marketplaceEntity) === 6050;
  const shouldAddMortgageUtm =
    dataDependency.type === DATA_DEPENDENCY_TYPES.MORTGAGES &&
    slug === 'awards-mortgages-2022' &&
    !isNonMonetizing(marketplaceEntity);
  const applyLink = getApplyLink(marketplaceEntity);
  const delimeter = applyLink && applyLink.includes('?') ? '&' : '?';
  const prevLinkPropsCheck = shouldUpdateSharedId
    ? {
        ctaHref: `${applyLink}${delimeter}sharedid=4664157C`,
      }
    : {};
  const linkProps = shouldAddMortgageUtm
    ? {
        ctaHref: `${applyLink}${delimeter}partnerParams=utm_campaign%3Dnerdwallet_content`,
      }
    : prevLinkPropsCheck;

  // Omit tabs as needed.
  const omittedTabs = [
    // Exclude 'why we like it' tab since it largely duplicates the
    // `why this product` section.
    CONTENT_TABS.WHY_WE_LIKE_IT,
  ];

  const renderWhyThisProduct = () => (
    <div
      className={classNames([
        styles['why-this-product'],
        nds('margin-bottom-2'),
      ])}
    >
      <Text size="small" component="h4">
        Why this{' '}
        {whyThisProductTypeOverride ||
          getProductName(marketplaceEntity) ||
          'product'}{' '}
        made the list
      </Text>
      <Text component="p">{whyThisProductText}</Text>
      <Link
        target="_blank"
        href={setTrk({
          url: whyThisProductMethodologyUrl,
          params: {
            [TrkTag.copy]: 'Read our methodology',
            [TrkTag.element]: TrkElement.hyperlink,
            [TrkTag.location]: 'MarketplaceProductCard__whyThisProduct',
            trk_productId: getId(marketplaceEntity),
          },
        })}
      >
        Read our methodology
      </Link>
    </div>
  );

  const renderWhyNerdsLoveIt = () => (
    <div
      className={classNames([styles.whyNerdsLoveIt, nds('margin-bottom-2')])}
    >
      <Text size="small" component="h4">
        Why our Nerds love it
      </Text>
      <Text component="p">{whyNerdsLoveItText}</Text>
      <Link
        target="_blank"
        href={setTrk({
          url: whyNerdsLoveItMethodologyUrl,
          params: {
            [TrkTag.copy]: 'Read our methodology',
            [TrkTag.element]: TrkElement.hyperlink,
            [TrkTag.location]: 'MarketplaceProductCard__whyThisProduct',
            trk_productId: getId(marketplaceEntity),
          },
        })}
      >
        Read our methodology
      </Link>
    </div>
  );

  const expandableContentProps = { entityName: 'marketplace_product_card' };

  const analytics = {
    enabled: true,

    // empty object required for product_impression
    productImpressionProps: {},

    // expandableContentProps is duplicated because MPC takes separate props
    // for desktop vs. mobile
    contentProps: { expandableContentProps },
    expandableContentProps,
  };

  if (!isUnstructuredVertical) {
    return (
      <div
        style={{ scrollMarginTop: `${scrollMargin || 0}px` }}
        id={identifier}
        className={classNames(
          styles.marketplaceProductCard,
          styles[marginTop] || nds(marginTop),
          styles[marginBottom] || nds(marginBottom),
          {
            // If a user don't set custom marginTop/Bottom we set default value
            [styles.marketplaceProductCardTopOffset]: !marginTop,
            [styles.marketplaceProductCardBottofOffset]: !marginBottom,
          }
        )}
      >
        <MarketplaceProductCardImpression
          analytics={analytics}
          displayPage={DISPLAY_PAGES.DIGITAL_MARKETING}
          hasAccordionOnMobile
          identity={identity}
          marketplaceEntity={marketplaceEntity}
          omitRating={omitRating}
          omittedTabs={_.compact([
            ...omittedTabs,
            // Omit `key facts` tab for Personal Loans.
            getProductType(marketplaceEntity) === PRODUCT_TYPES.PERSONAL_LOANS
              ? CONTENT_TABS.KEY_FACTS
              : undefined,
          ])}
          {...compareButtonProps}
          {...headingProps}
          {...badgeProps}
          {...linkProps}
        />
        {includeWhyThisProduct ? renderWhyThisProduct() : null}
        {includeWhyNerdsLoveIt ? renderWhyNerdsLoveIt() : null}
      </div>
    );
  }

  /**
   * MarketplaceProductCard uses the 'ProductCtas' and 'MarketplaceApplyNow' components, which create
   * an impression event based off of the 'marketplaceEntity' object (which unstructured
   * verticals do not have).
   * In order to track the 'ProductCtas'/'MarketplaceApplyNow' impression, the tracking event logic
   * assumes a 'vertical' property is set from the 'marketplaceEntity', and tries to set an
   * 'eventName' based off of this unset string which causes the MarketplaceProductCard to crash.
   * The fix is to pass 'shouldLogImpression: false' so that impressions are not logged through the
   * 'ProductCtas'/'MarketplaceApplyNow' components, and wrap unstructured verticals in
   * 'ElementImpression' in order to be tracked.
   * Link to a conversation about the problem: https://nerdwallet.slack.com/archives/CDPF7S56K/p1572472820003600
   */
  const keyFactsList = !_.isEmpty(keyFacts) ? (
    <List copyStyle="text-default">
      {keyFacts.map((keyFact) => (
        <Li key={keyFact.$unique_key}>{keyFact.text}</Li>
      ))}
    </List>
  ) : null;

  const productImage = productCardImage && {
    src: productCardImage,
    alt: heading,
  };

  return (
    <ElementImpression
      elementContent={cardIdentifier}
      elementName="MarketplaceProductCard"
      id={`MarketplaceProductCard--${heading}-${ctaHref}-${ctaText}`}
    >
      <div
        id={identifier}
        style={{ scrollMarginTop: `${scrollMargin}px` }}
        className={classNames(
          styles.marketplaceProductCard,
          styles[marginTop] || nds(marginTop),
          styles[marginBottom] || nds(marginBottom),
          {
            [styles.marketplaceProductCardTopOffset]: !marginTop,
            [styles.marketplaceProductCardBottofOffset]: !marginBottom,
          }
        )}
      >
        <BaseMarketplaceProductCard
          ctaHref={ctaHref}
          ctaSubtext={ctaSubtext}
          ctaText={ctaText}
          displayPage={DISPLAY_PAGES.DIGITAL_MARKETING}
          hasAccordionOnMobile
          heading={heading}
          identity={identity}
          imageData={productImage}
          keyFacts={keyFactsList}
          omitRating={omitRating}
          omittedTabs={omittedTabs}
          productType={productType}
          shouldLogImpression={false}
          // star Rating is saved as a string
          // and must be passed as a number to ReactMarketplaceProductCard
          starRating={_.toNumber(starRating)}
          {...badgeProps}
        />
        {includeWhyThisProduct ? renderWhyThisProduct() : null}
        {includeWhyNerdsLoveIt ? renderWhyNerdsLoveIt() : null}
      </div>
    </ElementImpression>
  );
};

MarketplaceProductCard.propTypes = {
  badgeText: PropTypes.string,
  ctaHref: PropTypes.string,
  ctaSubtext: PropTypes.string,
  ctaText: PropTypes.string,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
  dataDependency: PropTypes.shape({
    type: PropTypes.string,
    ids: PropTypes.string,
    productPage: PropTypes.string,
  }),
  hasWhyThisProduct: PropTypes.bool,
  heading: PropTypes.string,
  history: PropTypes.object,
  identity: PropTypes.object,
  includeBadge: PropTypes.bool,
  includeWhyThisProduct: PropTypes.bool,
  isUnstructuredVertical: PropTypes.bool,
  keyFacts: PropTypes.arrayOf(PropTypes.string),
  omitRating: PropTypes.bool,
  productCardImage: PropTypes.string,
  productType: PropTypes.oneOf(_.values(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL)),
  starRating: PropTypes.string,
  whyThisProductMethodologyUrl: PropTypes.string,
  whyThisProductText: PropTypes.string,
  whyThisProductTypeOverride: PropTypes.string,
  saveToCompare: PropTypes.func,
  removeFromCompare: PropTypes.func,
  includeCompareButton: PropTypes.bool,
  includeWhyNerdsLoveIt: PropTypes.bool,
  whyNerdsLoveItMethodologyUrl: PropTypes.string,
  whyNerdsLoveItText: PropTypes.string,
  filterByQueryParam: PropTypes.bool,
  queryParamName: PropTypes.string,
  queryParamValue: PropTypes.string,
  includeMarginTop: PropTypes.bool,
  marginTopDesktop: PropTypes.string,
  marginTopTablet: PropTypes.string,
  marginTopMobile: PropTypes.string,
  includeMarginBottom: PropTypes.bool,
  marginBottomDesktop: PropTypes.string,
  marginBottomTablet: PropTypes.string,
  marginBottomMobile: PropTypes.string,
  size: PropTypes.string,
  identifier: PropTypes.string,
  scrollOffsetDesktop: PropTypes.number,
  scrollOffsetTablet: PropTypes.number,
  scrollOffsetMobile: PropTypes.number,
  location: PropTypes.object,
};

MarketplaceProductCard.defaultProps = {
  badgeText: '',
  data: {},
  dataDependency: {},
  hasWhyThisProduct: false,
  history: {},
  identity: {},
  includeBadge: false,
  isUnstructuredVertical: false,
  omitRating: false,
  includeCompareButton: false,
};

MarketplaceProductCard.getEditingConfig = (componentDocument) => {
  const isUnstructuredVertical = _.get(
    componentDocument,
    'props.isUnstructuredVertical',
    false
  );
  const isBanking =
    _.get(componentDocument, 'dataDependency.type') === PRODUCT_TYPES.BANKING;
  const headingInputConfig = stringInputSpec('heading', 'Product Name');
  const unstructedVerticalConfig = [
    stringSelectSpec(
      'productType',
      'Product type',
      _.map(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL, (productType) => ({
        label: _.startCase(productType),
        value: productType,
      }))
    ),
    headingInputConfig,
    stringInputSpec('productCardImage', 'Product card image'),
    stringInputSpec('ctaHref', 'CTA href'),
    stringInputSpec('ctaText', 'CTA text'),
    stringInputSpec('ctaSubtext', 'CTA subtext'),
    repeatable('keyFacts', 'Key Facts', 'keyFact', () => [
      stringInputSpec('text', 'Key Fact'),
    ]),
  ];
  const dataDependencyConfig = [
    dataDependencyTypePicker,
    dataDependencyIdInput,
    !isBanking &&
      boolCheckboxSpec(
        'includeCompareButton',
        'Add compare credit card button?'
      ),
  ];
  const includeBadge = _.get(componentDocument, 'props.includeBadge', false);
  const badgeConfig = [
    boolCheckboxSpec('includeBadge', 'Include Best-of Badge?'),
    includeBadge && stringInputSpec('badgeText', 'Badge Text'),
  ];

  const includeWhyThisProduct = _.get(
    componentDocument,
    'props.includeWhyThisProduct',
    false
  );
  const whyThisProductConfig = [
    boolCheckboxSpec(
      'includeWhyThisProduct',
      "Include 'Why this product' section?",
      {
        help: 'Include a section below the product card explaining why this product is a Best of Award',
      }
    ),
    ...(includeWhyThisProduct
      ? [
          stringInputSpec('whyThisProductText', 'Product explanation', {
            help: 'Short text explaining why this product made the list',
          }),
          stringInputSpec('whyThisProductMethodologyUrl', 'Methodology URL'),
          stringInputSpec(
            'whyThisProductTypeOverride',
            'Override product type',
            {
              help: "Override the category name to use in the 'Why this product' title",
            }
          ),
        ]
      : []),
  ];
  // We use it in withFilters HOC
  const queryParamsConfig = [
    boolCheckboxSpec('filterByQueryParam', 'Reflect on query params changes?', {
      help: 'If a user uses a different query param we will hide the card',
    }),
    ...(_.get(componentDocument, 'props.filterByQueryParam', false)
      ? [
          stringInputSpec('queryParamName', 'Query param name', {
            help: 'Which query param we should use',
          }),
          stringInputSpec('queryParamValue', 'Query param value', {
            help: `
              The list of the possible values separated by |. When a queryParam with the queryParamName
              contains one of the possible values we show the entity
            `,
          }),
        ]
      : []),
  ];

  const createMarginSelect = (margin, device) =>
    stringSelectSpec(
      _.camelCase(`${margin}-${device}`),
      `${margin} for ${device}`,
      [
        { label: 'Unselected', value: '' },
        ...new Array(6).fill(null).map((ignoredValue, index) => ({
          label: `${margin} ${index + 1}`,
          value: `${margin}-${index + 1}`,
        })),
      ]
    );
  const createMarginOptions = (property) => {
    const checkboxName = `include${property
      .split('-')
      .map(_.capitalize)
      .join('')}`;
    const includeMargin = _.get(
      componentDocument,
      `props.${checkboxName}`,
      false
    );
    return [
      boolCheckboxSpec(checkboxName, `Output ${property}`),
      ...(includeMargin
        ? ['desktop', 'tablet', 'mobile'].map((device) =>
            createMarginSelect(property, device)
          )
        : []),
    ];
  };

  const includeWhyNerdsLoveIt = _.get(
    componentDocument,
    'props.includeWhyNerdsLoveIt',
    false
  );
  const whyNerdsLoveItConfig = [
    boolCheckboxSpec(
      'includeWhyNerdsLoveIt',
      "Include 'Why our Nerds Love It' section?",
      {
        help: 'Include a section below the product card explaining why our Nerds Love it',
      }
    ),
    ...(includeWhyNerdsLoveIt
      ? [
          stringInputSpec('whyNerdsLoveItText', 'Explanation', {
            help: 'Short text explaining why our Nerds Love It',
          }),
          stringInputSpec('whyNerdsLoveItMethodologyUrl', 'Methodology URL'),
        ]
      : []),
  ];

  return [
    group('Product info', [
      boolCheckboxSpec(
        'isUnstructuredVertical',
        'Is this an unstructured vertical?',
        {
          help: 'Check to manually input information for a vertical without data in marketplace (Taxes, Travel, Auto Insurance)',
        }
      ),
      // Show 1. unstructured vertical fields or 2. data dependency fields.
      ...(isUnstructuredVertical
        ? unstructedVerticalConfig
        : dataDependencyConfig),
      // Show heading override field if BNK and not already showing for unstructured vertical.
      !isUnstructuredVertical && isBanking && headingInputConfig,
    ]),
    group('Ratings + badges', [
      !isUnstructuredVertical &&
        boolCheckboxSpec('omitRating', 'Omit star rating?'),
      isUnstructuredVertical && stringInputSpec('starRating', 'Star rating'),
      ...badgeConfig,
    ]),
    group('Card footer', [...whyThisProductConfig, ...whyNerdsLoveItConfig]),
    group('Jump link', [
      stringInputSpec('identifier', 'Jump link ID', {
        help: 'You can specify a unique Jump Link ID for this product card to navigate directly to the spot on the page where this card appears',
      }),
      ...(_.get(componentDocument, 'props.identifier', false)
        ? ['desktop', 'tablet', 'mobile'].map((device) =>
            numberInputSpec(
              _.camelCase(`scroll-offset-${device}`),
              `Scroll offset for ${device}`,
              {
                help: `Add additional scroll offset for ${device} devices (use to increase top padding and push content down from location of Jump link ID)`,
              }
            )
          )
        : []),
    ]),
    group('Filters', [...queryParamsConfig]),
    group('Margins', [
      ...createMarginOptions('margin-top'),
      ...createMarginOptions('margin-bottom'),
    ]),
  ].filter(Boolean); // Remove empty/falsey values to ensure test parity.
};

const dataDependencies = (props) => {
  const type = _.get(props, 'dataDependency.type');
  if (!type) {
    return null;
  }

  const id = _.get(props, 'dataDependency.data.ids[0].id');
  const ids = id ? [id] : [];

  return {
    data: {
      type,
      ids,
    },
  };
};

const mapDispatchToProps = {
  saveToCompare: compareCreditCardsBarActions.saveToCompare,
  removeFromCompare: compareCreditCardsBarActions.removeFromCompare,
};

const WrappedComponent = compose(
  withFilters,
  withMarketplaceContext,
  withOutOfDateMessaging,
  connect(
    createStructuredSelector({
      size: _.property('mediaQuery'),
      identity: _.property('identity'),
    }),
    mapDispatchToProps
  )
)(MarketplaceProductCard);

export default withDataDependencies(WrappedComponent, dataDependencies);
